import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  type Observable,
  of,
  repeat,
  Subscription,
  switchMap,
  timer,
} from 'rxjs';

import type {AppError} from '../../core/types';
import type {
  ControllerError,
  ControllerResult,
  ControllerSuccess,
  ConversationOverview,
  ListConversationFilters,
} from '../conversations/types';
import type {ConversationsController} from '../app/types';
import {isConversationOverviewEnded} from '../conversations/conversationSelectors';
import {ThreadsViewModel} from './ThreadsViewModel';

export const DEFAULT_HISTORY_LIMIT = 20;
export const BACKGROUND_POLL_INTERVAL_MS = 30_000;

export type HistoryViewModelState = {
  isOpen: boolean;
  overviews: ConversationOverview[];
  loading: boolean;
  loaded: boolean;
  error: AppError | ControllerError | null;
  currentConversationId: string;
};

type HistoryViewModelOptions = {
  conversations: ConversationsController;
  // Refresh history in the background while this emits true.
  backgroundSyncEnabled$?: Observable<boolean>;
  getCurrentConversationId: () => string;
  selectConversation: (id: string) => void;
};

const unknownError = (error: unknown): ControllerError => ({
  type: 'error',
  error: error instanceof Error ? error.message : 'Unknown history error',
});

const sortByLastUpdatedDesc = (
  overviews: ConversationOverview[],
): ConversationOverview[] =>
  [...overviews].sort((a, b) => b.last_updated_at - a.last_updated_at);

export class HistoryViewModel extends ThreadsViewModel<HistoryViewModelState> {
  private _conversations: ConversationsController;

  private _getCurrentConversationId: () => string;

  private _selectConversation: (id: string) => void;

  private _backgroundSyncEnabled$: Observable<boolean>;

  private _subscriptions?: Subscription;

  constructor({
    conversations,
    backgroundSyncEnabled$ = of(false),
    getCurrentConversationId,
    selectConversation,
  }: HistoryViewModelOptions) {
    super({
      isOpen: false,
      overviews: [],
      loading: false,
      loaded: false,
      error: null,
      currentConversationId: getCurrentConversationId(),
    });

    this._conversations = conversations;
    this._backgroundSyncEnabled$ = backgroundSyncEnabled$;
    this._getCurrentConversationId = getCurrentConversationId;
    this._selectConversation = selectConversation;
  }

  activate() {
    if (this._subscriptions) {
      return;
    }
    const subscriptions = new Subscription();
    this._subscriptions = subscriptions;
    subscriptions.add(
      this._conversations
        .overviewChangesObservable()
        .subscribe((overview) => this.projectOverview(overview)),
    );
    subscriptions.add(
      this._backgroundSyncEnabled$
        .pipe(
          distinctUntilChanged(),
          switchMap((enabled) =>
            enabled ? this.backgroundSyncResults() : EMPTY,
          ),
        )
        .subscribe((overviews) => this.syncOverviews(overviews)),
    );

    const overviews = this.state.overviews.map(
      (overview) => this._conversations.getOverview(overview.id) ?? overview,
    );
    this.setState({
      overviews: this._withPinnedCurrent(sortByLastUpdatedDesc(overviews)),
    });
  }

  deactivate() {
    this._subscriptions?.unsubscribe();
    this._subscriptions = undefined;
  }

  // Each poll waits a full interval after the previous request settles.
  // Unsubscribing drops any in-flight result.
  private backgroundSyncResults(): Observable<ConversationOverview[]> {
    return timer(BACKGROUND_POLL_INTERVAL_MS).pipe(
      switchMap(() =>
        from(
          this._conversations.list({
            limit: DEFAULT_HISTORY_LIMIT,
            status: 'active',
          }),
        ).pipe(catchError(() => EMPTY)),
      ),
      filter(
        (result): result is ControllerSuccess<ConversationOverview[]> =>
          result.type === 'success',
      ),
      map((result) => result.data),
      repeat(),
    );
  }

  open = () => {
    this.setState({
      isOpen: true,
      currentConversationId: this._getCurrentConversationId(),
    });
    this.load();
  };

  close = () => {
    this.setState({
      isOpen: false,
      currentConversationId: this._getCurrentConversationId(),
    });
  };

  back = this.close;

  load = async (
    filters?: ListConversationFilters,
  ): ControllerResult<ConversationOverview[]> => {
    if (this.state.loading) {
      return {
        type: 'success',
        data: this.state.overviews,
      };
    }

    try {
      this.setState({
        loading: true,
        error: null,
        currentConversationId: this._getCurrentConversationId(),
      });

      const response = await this._conversations.list({
        limit: DEFAULT_HISTORY_LIMIT,
        status: 'active',
        ...filters,
      });

      if (response.type === 'error') {
        this.setState({
          loading: false,
          error: response,
        });
        return response;
      }

      const overviews = this.setLoadedOverviews(response.data, filters);
      return {
        ...response,
        data: overviews,
      };
    } catch (error) {
      const controllerError = unknownError(error);
      this.setState({
        loading: false,
        error: controllerError,
        currentConversationId: this._getCurrentConversationId(),
      });

      return controllerError;
    }
  };

  // Populate the drawer from an existing query without issuing another list.
  setLoadedOverviews(
    incoming: ConversationOverview[],
    filters?: ListConversationFilters,
  ): ConversationOverview[] {
    const isActiveHistoryQuery =
      (filters?.status ?? 'active') === 'active' &&
      filters?.conversationIds == null;
    const projected = incoming.map(
      (overview) => this._conversations.getOverview(overview.id) ?? overview,
    );
    const sorted = sortByLastUpdatedDesc(projected);
    const overviews = isActiveHistoryQuery
      ? this._withPinnedCurrent(sorted)
      : sorted;
    this.setState({
      overviews,
      loading: false,
      loaded: true,
      error: null,
      currentConversationId: this._getCurrentConversationId(),
    });
    return overviews;
  }

  // The backend list filter can briefly drop the conversation the user is
  // currently viewing during the live-chat connecting window (it has left
  // the bot conversation but the chat hasn't connected yet). Re-add it here
  // so it doesn't flicker out of the history list on load()/open().
  private _withPinnedCurrent(
    overviews: ConversationOverview[],
  ): ConversationOverview[] {
    const currentConversationId = this._getCurrentConversationId();
    const current = this._conversations.getOverview(currentConversationId);
    if (
      current == null ||
      current.id !== currentConversationId ||
      isConversationOverviewEnded(current) ||
      overviews.some((o) => o.id === current.id)
    ) {
      return overviews;
    }
    return sortByLastUpdatedDesc([...overviews, current]);
  }

  projectOverview(overview: ConversationOverview): void {
    const index = this.state.overviews.findIndex(
      (item) => item.id === overview.id,
    );
    const shouldPin =
      overview.id === this._getCurrentConversationId() &&
      !isConversationOverviewEnded(overview);
    if (index === -1 && !shouldPin) {
      return;
    }
    if (index !== -1 && this.state.overviews[index] === overview) {
      return;
    }

    const overviews = [...this.state.overviews];
    if (index === -1) {
      overviews.push(overview);
    } else {
      overviews[index] = overview;
    }
    this.setState({overviews: sortByLastUpdatedDesc(overviews)});
  }

  syncOverviews(incoming: ConversationOverview[]): ConversationOverview[] {
    if (incoming.length === 0) {
      return incoming;
    }
    const projected = incoming.map(
      (overview) => this._conversations.getOverview(overview.id) ?? overview,
    );
    const merged = new Map(this.state.overviews.map((o) => [o.id, o]));
    for (const overview of projected) {
      merged.set(overview.id, overview);
    }
    this.setState({overviews: sortByLastUpdatedDesc([...merged.values()])});
    return projected;
  }

  selectConversation = (id: string) => {
    const currentConversationId = this._getCurrentConversationId();
    this.setState({
      isOpen: false,
      currentConversationId,
    });

    if (id !== currentConversationId) {
      this._selectConversation(id);
    }
  };
}
